import React, { useEffect, useState, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../services/firebase";
import { ReactComponent as SearchIcon } from '../../assets/icons/SearchIcon.svg';
import { getAuth } from 'firebase/auth';
import { ReactComponent as InventarioIcon } from '../../assets/icons/InventarioIcon.svg'
import LoadingSpinner from "../LoadingSpinner";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';


const getAbbreviation = (unit) => {
  const safeUnit = unit || "Unidad"; // Si viene undefined, null o "", toma "Unidad"
  
  const abbreviations = {
    Unidad: "u",
    Kilos: "kg",
    Gramos: "gr",
    Litro: "lts",
    Centilitros: "cl",
    Mililitros: "ml",
  };
  return abbreviations[safeUnit] || safeUnit;
};
const InventarioForm = () => {
const [searchText, setSearchText] = useState("");
const [stockDocs, setStockDocs] = useState([]);
const [proveedores, setProveedores] = useState({});
const [filteredDocs, setFilteredDocs] = useState([]);
const [openDropdown, setOpenDropdown] = useState(false);
const [selectedItems, setSelectedItems] = useState([]);
const [loading, setLoading] = useState(true);
const [modifiedItems, setModifiedItems] = useState([]);
const [isSubmitting, setIsSubmitting] = useState(false);
const [submitError, setSubmitError] = useState(null);
const containerRef = useRef(null);
const [buttonStatus, setButtonStatus] = useState({});
const prevSelectedItemsRef = useRef(selectedItems);
const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
useEffect(() => {
const allItemsAreModified = selectedItems.length > 0 && selectedItems.every(
(item) => buttonStatus[item.id] === 'modificado'
);
setIsSubmitDisabled(!allItemsAreModified || isSubmitting);
}, [selectedItems, buttonStatus, isSubmitting]);
const handleSubmit = async () => {
if (modifiedItems.length === 0) {
setSubmitError("No hay elementos para modificar.");
return;
}
const allItemsModified = selectedItems.every(
(item) => modifiedItems.some(modifiedItem => modifiedItem.id === item.id)
);
if (!allItemsModified) {
setSubmitError("Por favor, modifica todos los elementos de la lista antes de enviar.");
return;
}
setIsSubmitting(true);
setSubmitError(null);
const user = getAuth().currentUser;
if (!user) {
setIsSubmitting(false);
setSubmitError("Usuario no autenticado. Por favor, inicia sesión.");
return;
}
try {
const idToken = await user.getIdToken();
const datosParaEnviar = {
itemsModificados: modifiedItems.map(item => ({
...item,
diferencia: item.diferencia || 0 // asegura que diferencia sea 0 en lugar de "" o undefined
})),
};
const response = await fetch('https://backend-288175208699.europe-west1.run.app/modificar-inventario', {
method: 'POST',
headers: {
'Content-Type': 'application/json',
Authorization: `Bearer ${idToken}`,
},
body: JSON.stringify(datosParaEnviar),
});
if (!response.ok) {
const errorData = await response.json();
throw new Error(errorData.message || "Error al modificar el inventario.");
}
setModifiedItems([]);
setButtonStatus({});
alert("Inventario modificado con éxito!");
} catch (error) {
console.error("Error al enviar los datos:", error);
setSubmitError(error.message);
} finally {
setIsSubmitting(false);
}
};
const handleMotivoChange = (id, newMotivo) => {
setSelectedItems(prevItems =>
prevItems.map(item =>
item.id === id
? { ...item, motivo: newMotivo, openMotivo: false }
: item
)
);
};
useEffect(() => {
const fetchProveedores = async () => {
try {
const snapshot = await getDocs(collection(db, "proveedores"));
const data = {};
snapshot.forEach((doc) => {
data[doc.id] = doc.data().razonSocial || "Sin razón social";
});
setProveedores(data);
} catch (error) {
console.error("Error al traer proveedores:", error);
}
};
fetchProveedores();
}, []);
useEffect(() => {
const fetchStock = async () => {
try {
const querySnapshot = await getDocs(collection(db, "stock"));
const docs = querySnapshot.docs
  .filter((doc) => !doc.id.startsWith("DEMO-"))
  .map((doc) => ({
    id: doc.id,
    ...doc.data(),
    tipoUnidad: doc.data().tipoUnidad || "Unidad", // Valor por defecto
  }));
docs.sort((a, b) =>
(a.nombre || a.id).localeCompare(b.nombre || b.id, "es", { sensitivity: "base" })
);
setStockDocs(docs);
setFilteredDocs(docs);
} catch (error) {
console.error("Error al traer stock:", error);
} finally {
setLoading(false);
}
};
fetchStock();
}, []);
useEffect(() => {
if (searchText.trim() === "") {
setFilteredDocs(stockDocs);
} else {
const lowerSearch = searchText.toLowerCase();
setFilteredDocs(
stockDocs.filter((doc) =>
// Compara solo nombre o id desde el inicio
(doc.nombre && doc.nombre.toLowerCase().startsWith(lowerSearch)) ||
(doc.id && doc.id.toLowerCase().startsWith(lowerSearch))
)
);
}
}, [searchText, stockDocs]);



useEffect(() => {
const handleClickOutside = (event) => {
if (containerRef.current && !containerRef.current.contains(event.target)) {
setOpenDropdown(false);
}
};
document.addEventListener("mousedown", handleClickOutside);
return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);
const handleSelect = (doc) => {
const isItemInList = selectedItems.some(item => item.id === doc.id);
if (!isItemInList) {
setSelectedItems(prevItems => [...prevItems, { ...doc, conteo: "", motivo: null }]);
}
setSearchText("");
setOpenDropdown(false);
};
const handleCountChange = (id, newCount) => {
setSelectedItems(prevItems =>
prevItems.map(item => {
if (item.id === id) {
// Modifica esta línea para que el valor sea null si el campo está vacío.
const updatedItem = {
...item,
conteo: newCount === "" ? null : Number(newCount)
};
const difference = updatedItem.conteo === null ? "" : updatedItem.cantidadUnidades - updatedItem.conteo;
if (difference === 0) {
updatedItem.motivo = null;
}
return updatedItem;
}
return item;
})
);
};
const handleModify = (itemToModify) => {
  const simplifiedItem = {
    id: itemToModify.id,
    nombre: itemToModify.nombre,
    cantidadUnidades: itemToModify.cantidadUnidades,
    tipoUnidad: itemToModify.tipoUnidad || "Unidad", // Fallback a "Unidad"
    conteo: itemToModify.conteo,
    diferencia: itemToModify.cantidadUnidades - itemToModify.conteo,
    motivo: itemToModify.motivo,
    categoria: itemToModify.categoria
  };
const isAlreadyModified = modifiedItems.some(
(item) => item.id === simplifiedItem.id
);
if (!isAlreadyModified) {
setModifiedItems((prevItems) => [...prevItems, simplifiedItem]);
} else {
setModifiedItems((prevItems) =>
prevItems.map((item) =>
item.id === simplifiedItem.id ? simplifiedItem : item
)
);
}
setButtonStatus(prevStatus => ({ ...prevStatus, [itemToModify.id]: 'modificado' }));
};
const handleRemoveItem = (itemId) => {
setSelectedItems(prevItems => prevItems.filter(item => item.id !== itemId));
setModifiedItems(prevItems => prevItems.filter(item => item.id !== itemId));
setButtonStatus(prevStatus => {
const newStatus = { ...prevStatus };
delete newStatus[itemId];
return newStatus;
});
};
useEffect(() => {
const prevSelectedItems = prevSelectedItemsRef.current;
if (prevSelectedItems.length > 0) {
selectedItems.forEach(currentItem => {
const prevItem = prevSelectedItems.find(item => item.id === currentItem.id);
if (prevItem && (prevItem.conteo !== currentItem.conteo || prevItem.motivo !== currentItem.motivo)) {
setButtonStatus(prevStatus => ({ ...prevStatus, [currentItem.id]: null }));
}
});
}
prevSelectedItemsRef.current = selectedItems;
}, [selectedItems]);
return (
<div style={{padding: '0 16px'}}>
<header className="trazabilidad-header">
<div className="header-content">
<h1>Inventario</h1>
</div>
</header>
<div className="container" style={{ margin: '0 10px' }}>
<div className="formtitle-container">
<div className="formicon-container">
<InventarioIcon className="formicon" style={{ height: '17px', margin: 'auto' }} />
</div>
<h1 className="formtitle">Generar control de stock</h1>
</div>
<hr className="divider" />
<div className="inventario-search-bar-container" ref={containerRef}>
{loading ? (
<LoadingSpinner/>
) : (
<>
<div className={`inventario-search-input-wrapper ${openDropdown ? 'active' : ''}`}>
<div className="inventario-search-icon-wrapper">
<SearchIcon className="inventario-search-icon" />
</div>
<input
type="text"
placeholder="Buscar un articulo"
value={searchText}
onChange={(e) => {
setSearchText(e.target.value);
setOpenDropdown(true);
}}
onFocus={() => setOpenDropdown(true)}
className="inventario-search-input"
/>
</div>
{openDropdown && (
<div className="inventario-search-dropdown-options">
{filteredDocs.length > 0 ? (
filteredDocs.map((doc) => (
<div
key={doc.id}
className="inventario-search-dropdown-option"
onClick={() => handleSelect(doc)}
>
{doc.nombre || doc.id}
{doc.proveedorAsociado && proveedores[doc.proveedorAsociado] && (
<span className="inventario-search-dropdown-option-proveedor">
{" "}— {proveedores[doc.proveedorAsociado]}
</span>
)}
</div>
))
) : (
<div className="inventario-search-dropdown-option">
No se encontraron resultados
</div>
)}
</div>
)}
</>
)}
</div>
{selectedItems.length > 0 && (
<div className="inventario-container">
<div className="inventario-table-wrapper">
<table className="inventario-table">
<thead>
<tr className="inventario-header-row">
<th className="inventario-header-cell">Categoría</th>
<th className="inventario-header-cell">Nombre</th>
<th className="inventario-header-cell">Proveedor</th>
<th className="inventario-header-cell">Existencias actuales</th>
<th className="inventario-header-cell">Conteo</th>
<th className="inventario-header-cell">Diferencia</th>
<th className="inventario-header-cell">Motivo</th>
<th className="inventario-header-cell"></th>
<th className="inventario-header-cell"></th>
</tr>
</thead>
<tbody className="inventario-body">
{selectedItems.map((item) => {
const difference =
item.conteo === "" ? "" : item.cantidadUnidades - item.conteo;
const isModified = buttonStatus[item.id] === 'modificado';
const isMotivoDisabled = difference === 0 || difference === "";
return (
<tr key={item.id} className="inventario-row">
<td className="inventario-cell">{item.categoria}</td>
<td className="inventario-cell" style={{
maxWidth: '50px',
whiteSpace: 'nowrap',
overflow: 'hidden',
textOverflow: 'ellipsis',
}}>{item.nombre || item.id}</td>
<td className="inventario-cell" style={{
maxWidth: '50px',
whiteSpace: 'nowrap',
overflow: 'hidden',
textOverflow: 'ellipsis',
}}>
{proveedores[item.proveedorAsociado] || ""}
</td>
<td className="inventario-cell">
{item.cantidadUnidades} {getAbbreviation(item.tipoUnidad)}
</td>
<td className="inventario-cell">
<input
type="number"
value={item.conteo}
onChange={(e) => handleCountChange(item.id, e.target.value)}
className="inventario-input"
/>
<span className="inventario-unit">
{getAbbreviation(item.tipoUnidad)}
</span>
</td>
<td
className={`inventario-cell ${
difference === ""
? ""
: difference === 0
? "inventario-diff-ok"
: "inventario-diff-error"
}`}
>
{difference === ""
? ""
: difference > 0
? `(${difference})`
: difference < 0
? Math.abs(difference)
: difference}{" "}
{difference === "" ? "" : getAbbreviation(item.tipoUnidad)}
</td>
<td className="inventario-cell">
<div className="motivo-dropdown-container">
<Listbox
value={item.motivo || ""}
onChange={(value) => handleMotivoChange(item.id, value)}
disabled={isMotivoDisabled}
>
{({ open }) => (
<>
<ListboxButton
className={`motivo-dropdown-button ${isMotivoDisabled ? 'disabled' : ''} ${open ? "open" : ""}`}
>
{item.motivo ? item.motivo.charAt(0).toUpperCase() + item.motivo.slice(1) : "Seleccionar"}
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="motivo-dropdown-options">
{[ "defecto", "desconocido","evento","merma", "regalo", "muestra", "ruptura","uso"].map((motivoOption) => (
<ListboxOption
key={motivoOption}
value={motivoOption}
className="motivo-dropdown-option"
>
{motivoOption.charAt(0).toUpperCase() + motivoOption.slice(1)}
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
</td>
<td className="inventario-cell">
<button
onClick={() => handleModify(item)}
className={`modificar-button ${isModified ? 'modificado' : ''}`}
disabled={isModified || typeof item.conteo !== 'number' || item.conteo === '' || (difference !== 0 && !item.motivo)}
>
{isModified ? 'Modificado' : 'Modificar'}
</button>
</td>
<td className="inventario-cell">
<button
onClick={() => handleRemoveItem(item.id)}
className="remove-button"
>
&times;
</button>
</td>
</tr>
);
})}
</tbody>
</table>
</div>
{submitError && (
<div className="error-message">
{submitError}
</div>
)}
</div>
)}
</div>
<button
onClick={handleSubmit}
disabled={isSubmitDisabled}
className={`inventario-button ${isSubmitDisabled ? 'disabled' : ''}`}
>
{isSubmitting ? 'Enviando...' : `Enviar cambios`}
</button>
</div>
);
};
export default InventarioForm;